import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { ConfigProvider, Layout } from "antd";
import CryptoJS from "crypto-js";
import { SocketProvider } from "../../context/SocketContext";
import { ChatSidebarCurrentScreenProvider } from "../../context/ChatSidebarScreenContext";
import { SettingSidebarCurrentScreenProvider } from "../../context/SettingSidebarScreenContext";
import { AudioPlayerProvider } from "../../context/AudioPlayerContext";
import { initSip, setCallerId } from "../../redux/slices/sipSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchContacts } from "../../redux/slices/contactsSlice";
import { useGetPhoneNumbersQuery } from "../../redux/services/api";
import AddNumberFlowModal from "../../components/common/AddNumberFlowModal";
import Sidebar from "../../components/common/Sidebar";
import E911Modal from "../../components/common/e911Modal";
import IncomingCall from "../../components/calls/IncomingCall";
import GroupCallScreen from "../../pages/Call/GroupCallScreen";
import CallScreen from "../../pages/Call/CallScreen";
import IncomingGroupCall from "../../components/calls/IncomingGroupCall";
import "./index.scss";
import { CRYPTO, META, SILENT_AUDIO_WAV_BASE64 } from "../../constants";
import { storageService } from "../../services/storageService";
import SipSocketBridge from "../../components/SipSocketBridge";

export const useAppDispatch: () => AppDispatch = useDispatch;

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
interface DecodedToken extends JwtPayload {
  key?: string;
}

function decryptData(encryptedData: string): string | null {
  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedData, CRYPTO.AES_KEY, {
      iv: CRYPTO.AES_IV,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8) || null;
  } catch {
    return null;
  }
}

const MainLayout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [token, setToken] = useState(storageService.getAccessToken());
  const [addNumberFlowModalOpen, setAddNumberFlowModalOpen] = useState(false);
  const sipInitRef = useRef(false);
  const { data: callerIdData } = useGetPhoneNumbersQuery();
  const callerIdValue = useSelector((state: RootState) => state.sip.callerId);

  // Audio Unlock mechanism for Autoplay policy
  useEffect(() => {
    const unlockAudio = () => {
      const audio = new Audio(SILENT_AUDIO_WAV_BASE64);
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
        })
        .catch((err) => {
          console.warn("Audio unlock failed", err);
        });
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };

    document.addEventListener("pointerdown", unlockAudio, { once: true });
    document.addEventListener("keydown", unlockAudio, { once: true });

    return () => {
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  //  Authguard
  useEffect(() => {
    if (!token) {
      navigate("auth/login", { replace: true });
    }
  }, [navigate, token]);

  //Cross-tab logout
  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key !== storageService.getAccessToken()) return; // Note: StorageEvent.key is the literal string key. 
      // Actually, storageService methods don't help with event.key matching if we use constants.
      // But we can compare event.key with the known value.
      if (event.key !== "access_token") return; 
      
      const newToken = storageService.getAccessToken();
      setToken(newToken);
      if (!newToken) navigate("/auth/login", { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [handleStorageChange]);

  // Phonenumber purchase flow modal
  useEffect(() => {
    const purchased = storageService.getIsNumberPurchased();
    const registered = storageService.getIsNumberRegistered();
    setAddNumberFlowModalOpen(!purchased && !registered);
  }, []);

  // Default caller ID
  useEffect(() => {
    if (callerIdValue || !callerIdData?.data?.length) return;
    const primary = callerIdData.data.find((p) => p.isPrimary);
    const number = primary?.phoneNumber ?? callerIdData.data[0]?.phoneNumber;
    if (number) dispatch(setCallerId(number));
  }, [callerIdData, callerIdValue, dispatch]);

  useEffect(() => {
    if (!token || sipInitRef.current) return;
    try {
      const decodedToken = jwtDecode<DecodedToken>(token);
      if (!decodedToken?.key) return;

      const decrypted = decryptData(decodedToken.key);
      if (!decrypted) return;

      const userData = JSON.parse(decrypted);
      if (!userData) return;

      if (userData.fullName) {
        storageService.setFullName(userData.fullName);
      }

      dispatch(
        initSip({
          uri: `sip:${userData.sipUsername}@${userData.webRTCServer}`,
          password: userData.sipPassword,
          server: `wss://${userData.webRTCDomain}:${userData.webRTCPort}`,
          registrar_server: userData.webRTCServer,
          authorization_user: userData.sipUsername,
        }),
      );

      storageService.setIsSupportAgent(userData.userGroup !== 2);
    } catch (err) {
      console.error("[MainLayout] SIP init error:", err);
      sipInitRef.current = false;
    }
  }, [token, dispatch]);

  useEffect(() => {
    dispatch(fetchContacts());
  }, [dispatch]);

  useEffect(() => {
    if (sessionStorage.getItem(META.SESSION_KEY)) return;
    sessionStorage.setItem(META.SESSION_KEY, "yes");
    if (
      !document.querySelector(
        'script[src*="connect.facebook.net/en_US/fbevents.js"]',
      )
    ) {
      (function (f: Window, b: Document, e: string, v: string) {
        if (f.fbq) return;
        const n = function (this: any, ...args: any[]) {
          if (n.callMethod) {
            n.callMethod.apply(n, args);
          } else {
            (n.queue = n.queue || []).push(args);
          }
        } as typeof window.fbq;

        n.queue = [];
        n.loaded = true;
        n.version = "2.0";

        f.fbq = n;
        f._fbq = n;

        const t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = v;
        const s = b.getElementsByTagName(e)[0];
        if (s.parentNode) {
          s.parentNode.insertBefore(t, s);
        }
      })(window, document, "script", META.EVENTS_URL);
    }

    window.fbq?.("init", META.PIXEL_ID);
    window.fbq?.("track", "PageView");
  }, []);

  return (
    <ConfigProvider>
      <AudioPlayerProvider>
      <SocketProvider>
        <SipSocketBridge />
        <ChatSidebarCurrentScreenProvider>
          <SettingSidebarCurrentScreenProvider>
            <div className="app-container">
              <Layout>
                <IncomingCall />
                <IncomingGroupCall />
                <GroupCallScreen />
                <CallScreen />
                <Sidebar />
                <Outlet />
                <E911Modal />
                <AddNumberFlowModal
                  visible={addNumberFlowModalOpen}
                  onClose={() => {}}
                />
              </Layout>
            </div>
          </SettingSidebarCurrentScreenProvider>
        </ChatSidebarCurrentScreenProvider>
      </SocketProvider>
      </AudioPlayerProvider>
    </ConfigProvider>
  );
};

export default MainLayout;
